import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { ChartEmptyState } from '@/ui/composites';
import { StatusPieCardSkeleton } from '@/ui/composites/status/StatusPieCardSkeleton';
import { Box, LineChartSkeleton, Skeleton } from '@/ui/primitives';
import type { ReactElement } from 'react';
export {
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.icons';

const LONG_SKELETON_HEIGHT = componentSizes.dataGrid.skeletonHeight;
const LONG_SKELETON_RADIUS = '999px';

export const renderMiniMetricEmptyState = (title: string, height: number): ReactElement => (
  <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <ChartEmptyState title={title} contentSx={{ transform: 'translateY(-6px)' }} />
  </Box>
);

export const renderMiniMetricLoadingState = (height: number): ReactElement => (
  <LineChartSkeleton
    height={height}
    leftInset={`calc(${s('md')} + ${s('none')})`}
    bottomInset={s('md')}
    showPlotAreaBackground={false}
    hideYAxisLine
  />
);

export const renderStatusPieLoadingState = (height: number): ReactElement => {
  const legendWidths = ['94%', '94%'] as const;
  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: s('2xl'),
        pt: s('2xl'),
      }}
    >
      <StatusPieCardSkeleton />
      <Box
        sx={{
          width: '100%',
          maxWidth: '72%',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: s('sm'),
          px: s('lg'),
          pt: s('md'),
          justifyContent: 'center',
          justifyItems: 'center',
          mx: 'auto',
        }}
      >
        {legendWidths.map((width, index) => (
          <Skeleton
            key={`pie-legend-skeleton-${index}`}
            variant="rounded"
            width={width}
            height={LONG_SKELETON_HEIGHT}
            sx={{
              display: 'block',
              flexShrink: 0,
              minHeight: LONG_SKELETON_HEIGHT,
              maxHeight: LONG_SKELETON_HEIGHT,
              borderRadius: LONG_SKELETON_RADIUS,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export const renderStatusChartLegendLoadingState = (): ReactElement => {
  const itemWidths = [118, 112, 130] as const;
  return (
    <Box
      sx={{
        width: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        columnGap: s('lg'),
        rowGap: s('xs'),
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {itemWidths.map((width, index) => (
        <Skeleton
          key={`status-legend-skeleton-${index}`}
          variant="rounded"
          width={width}
          height={LONG_SKELETON_HEIGHT}
          sx={{
            display: 'block',
            flexShrink: 0,
            minHeight: LONG_SKELETON_HEIGHT,
            maxHeight: LONG_SKELETON_HEIGHT,
            borderRadius: LONG_SKELETON_RADIUS,
          }}
        />
      ))}
    </Box>
  );
};

export const renderMiniMetricHeaderLoadingState = (): ReactElement => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: s('md'),
    }}
  >
    <Skeleton
      variant="rounded"
      width={92}
      height={LONG_SKELETON_HEIGHT}
      sx={{ borderRadius: LONG_SKELETON_RADIUS }}
    />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Skeleton
        variant="rounded"
        width={78}
        height={LONG_SKELETON_HEIGHT}
        sx={{ borderRadius: LONG_SKELETON_RADIUS }}
      />
    </Box>
  </Box>
);

export const formatStackedStatusChartLabel = (label: string): string => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}`;
  }
  const slashMatch = /^(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?$/.exec(trimmed);
  if (slashMatch) {
    const day = slashMatch[1].padStart(2, '0');
    const month = slashMatch[2].padStart(2, '0');
    return `${day}/${month}`;
  }
  return trimmed;
};
